import { Injectable } from '@angular/core';
import { Router }     from '@angular/router';

import { BehaviorSubject, Observable, of, tap, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { CookieService } from '@services/cookie.service';

import { environment } from '@environment/environment';

const SESSION_COOKIE_KEY = 'r1000Session';
const PARTNER_COOKIE_KEY = 'partnerId';

import { AuthUser }      from '@models/user/auth-user';
import { PidAuthParams } from '@models/user/pid-auth-params';
import { UserType }      from '@models/user/user-type';

import { ApiUserService }    from '@services/api-user.service';
import { ApiLocalNavigationMenuService } from '@services/api-local-navigation-menu.service';
import { HttpErrorResponse } from '@angular/common/http';


@Injectable({ providedIn: 'root' })
export class UserService {

  private readonly _activeUser$: Observable<AuthUser>;

  private _activeUserSource$: BehaviorSubject<AuthUser>;

  private _redirectRoute: ( string | number )[];
  private _contractAccepted: boolean;
  private _pidAuthParams: PidAuthParams;
  private _partnerId: number;
  private _hasUser: boolean;
  private _retrievingUser: boolean;
  private _authSettled$: BehaviorSubject<boolean>;

  public static getQueryParam(paramName: string): string {
    const regex: RegExp           = new RegExp(`[?&]${ paramName }(=([^&#]*)|&|#|$)`),
          result: RegExpExecArray = regex.exec(window.location.search);

    return result && result[ 2 ];
  }

  constructor(
    private router: Router,
    private apiUserService: ApiUserService,
    private apiLocalNavigationMenuService: ApiLocalNavigationMenuService,
    private cookieService: CookieService,
  ) {
    this._activeUserSource$ = new BehaviorSubject<AuthUser>(null);
    this._activeUser$       = this._activeUserSource$.asObservable();

    this._retrievingUser = false;
    this._authSettled$   = new BehaviorSubject<boolean>(false);

    this.resetRedirectRoute();
  }

  public initUser(): Observable<AuthUser> {
    if (this.hasUser || this._retrievingUser) return this.getActiveUser();

    this._retrievingUser = true;

    const callback = (user: AuthUser) => {
      this._retrievingUser = false;
      this._authSettled$.next(true);
      this.updateActiveUser(user);
    };

    const errorCallback = (error: HttpErrorResponse) => {
      this._retrievingUser = false;
      this._authSettled$.next(true);
      this.updateActiveUser(null);
      this.router.navigate([ '/home' ]).then();
      return throwError(() => error);
    };

    if (this.hasValidPidAuthParams()) {
      return this.apiUserService.pidAuthenticate(this.pidAuthParams).pipe(
        catchError(errorCallback), tap(callback)
      );
    }

    if (!this.isLoggedIn()) {
      this._retrievingUser = false;
      this._authSettled$.next(true);
      return of(null);
    }

    return this.apiUserService.getUser().pipe(
      catchError(errorCallback), tap(callback)
    );
  }

  public isLoggedIn(): boolean {
    return this.cookieService.check(SESSION_COOKIE_KEY);
  }

  private setSessionFlag(active: boolean): void {
    if (active) {
      this.cookieService.set(SESSION_COOKIE_KEY, 'true', {
        domain:   environment.cookieDomain,
        path:     '/',
        secure:   environment.production,
        sameSite: 'Lax',
      });
    } else {
      this.cookieService.delete(SESSION_COOKIE_KEY, { path: '/', domain: environment.cookieDomain });
      this.cookieService.delete(PARTNER_COOKIE_KEY, { path: '/', domain: environment.cookieDomain });
    }
  }

  public updateActiveUser(user: AuthUser): void {
    if (this.shouldInvalidateMenuCache(user)) {
      this.apiLocalNavigationMenuService.clearMenuCache();
    }

    this.setSessionFlag(!!user);

    this.partnerId        = user?.parceiroId || null;
    this.contractAccepted = !user?.aceiteContrato;
    this.hasUser          = !!user;

    this._activeUserSource$.next(user);
  }

  public getActiveUser(): Observable<AuthUser> {
    return this._activeUser$.pipe(distinctUntilChanged());
  }

  public whenAuthResolved(): Observable<AuthUser> {
    return this._authSettled$.pipe(
      filter(settled => settled),
      take(1),
      map(() => this._activeUserSource$.value)
    );
  }

  public getUserType(): Observable<UserType> {
    return this._activeUser$.pipe(
      filter((user: AuthUser) => !!user?.perfil),
      map((user: AuthUser) => user.perfil)
    );
  }

  public isUserAdmin(): Observable<boolean> {
    return this.getUserType().pipe(map((userType: UserType) => {
      return this.isUserTypeAdmin(userType);
    }));
  }

  public isUserSuperAdmin(): Observable<boolean> {
    return this.getUserType().pipe(map(this.isUserTypeSuperAdmin));
  }

  public isUserAdvisor(): Observable<boolean> {
    return this.getUserType().pipe(map((userType: UserType) => {
      return this.isUserTypeAdvisor(userType);
    }));
  }

  public isUserManager(): Observable<boolean> {
    return this.getUserType().pipe(map((userType: UserType) => {
      return this.isUserTypeManager(userType);
    }));
  }

  public isUserProofreader(): Observable<boolean> {
    return this.getUserType().pipe(map((userType: UserType) => {
      return this.isUserTypeProofreader(userType);
    }));
  }

  public isUserTypeAdmin(userType: UserType): boolean {
    return userType === 'ADMIN' || userType === 'SUPER_ADMIN';
  }

  public isUserTypeSuperAdmin(userType: UserType): boolean {
    return userType === 'SUPER_ADMIN';
  }

  public isUserTypeAdvisor(userType: UserType): boolean {
    return userType === 'ASSESSOR';
  }

  public isUserTypeManager(userType: UserType): boolean {
    return userType === 'COORDENADOR_ESCOLA' ||
      userType === 'COORDENADOR_TURMA' ||
      userType === 'COORDENADOR_SAAS' ||
      userType === 'CORRETOR_SAAS';
  }

  public isUserTypeProofreader(userType: UserType): boolean {
    return userType === 'PROFESSOR' || userType === 'CORRETOR_SAAS';
  }

  public get hasUser(): boolean { return this._hasUser; }

  public set hasUser(value: boolean) { this._hasUser = value; }

  public get contractAccepted(): boolean { return this._contractAccepted; }

  public set contractAccepted(value: boolean) { this._contractAccepted = value; }

  public get pidAuthParams(): PidAuthParams {
    if (this._pidAuthParams) return this._pidAuthParams;

    const pid: string               = UserService.getQueryParam('pid'),
          tid: string               = UserService.getQueryParam('tid'),
          usuarioParceiroId: string = UserService.getQueryParam('usuarioParceiroId') || null,
          token: string             = UserService.getQueryParam('token');

    this.pidAuthParams = { pid, tid, usuarioParceiroId, token };

    return this._pidAuthParams;
  }

  public set pidAuthParams(value: PidAuthParams) {
    this._pidAuthParams = value;
  }

  public hasValidPidAuthParams(): boolean {
    const params: PidAuthParams = this.pidAuthParams;
    return !!( params && params.pid && params.tid && params.token );
  }

  public set partnerId(partnerId: number) {
    this._partnerId = partnerId;
    if (partnerId) {
      this.cookieService.set(PARTNER_COOKIE_KEY, `${ partnerId }`, {
        domain:   environment.cookieDomain,
        path:     '/',
        secure:   environment.production,
        sameSite: 'Lax',
      });
    } else {
      this.cookieService.delete(PARTNER_COOKIE_KEY, { path: '/', domain: environment.cookieDomain });
    }
  }

  public get partnerId(): number {
    if (this._partnerId) return this._partnerId;

    const stored = this.cookieService.get(PARTNER_COOKIE_KEY);

    return stored ? +stored : null;
  }
  public get redirectRoute(): ( string | number )[] { return this._redirectRoute; }

  public set redirectRoute(route: ( string | number )[]) { this._redirectRoute = route; }

  public resetRedirectRoute(): void { this.redirectRoute = [ '/' ]; }

  public logout(): void {
    this.apiUserService.logout().subscribe({
      next: () => {
        // PLURALL_TRACKER: (window as any).PLURALL_TRACKER?.clearTrackingId();
        this.performLocalLogout();
      },
      error: () => {
        // API failed: do NOT call clearTrackingId(), just clean up locally
        this.performLocalLogout();
      }
    });
  }

  private shouldInvalidateMenuCache(nextUser: AuthUser): boolean {
    const currentUser: AuthUser = this._activeUserSource$.value;

    if (!currentUser && !nextUser) return false;
    if (!currentUser || !nextUser) return true;

    return currentUser.uuid !== nextUser.uuid
      || currentUser.perfil !== nextUser.perfil;
  }

  private performLocalLogout(): void {
    this.resetRedirectRoute();
    this.updateActiveUser(null);
    this.router.navigate([ '/home' ]).then();
  }
}
